// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@keller/App/Components/Common/ContactTypography";
import StyledBox, { StyledBoxStyles } from "@keller/App/Components/Common/StyledBox";
import { CustomerHomeBranchAddressInfo } from "@keller/XDL/types";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const homeBranchAddress = session.properties.customerHomeBranch
        ? (JSON.parse(session.properties.customerHomeBranch) as CustomerHomeBranchAddressInfo)
        : null;
    const locationFinderPageUrl = getPageLinkByPageType(state, "LocationFinderPage")?.url;
    const dealerId = homeBranchAddress?.dealerId;
    const locationFinderDetailsPageUrl = dealerId ? `${locationFinderPageUrl}/DealerDetails?id=${dealerId}` : null;
    const quoteState = getQuoteState(state, state.pages.rfqConfirmation.quoteId);
    return {
        locationFinderPageUrl,
        locationFinderDetailsPageUrl,
        warehouse: homeBranchAddress,
        quoteState,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface OrderConfirmationHelpTileStyles {
    styledBox?: StyledBoxStyles;
    title?: TypographyProps;
    description?: TypographyProps;
    contactWrapper?: InjectableCss;
    contactTypography?: TypographyProps;
    addressInfoDisplay?: AddressInfoCondensedDisplayStyles;
}

export const orderConfirmationHelpTileStyles: OrderConfirmationHelpTileStyles = {
    styledBox: {
        container: {
            css: css`
                padding: 17px;
                width: 100%;
                margin-bottom: 32px;
                @media print {
                    display: none;
                }
            `,
        },
    },
    title: {
        variant: "h4",
        css: css`
            &&& {
                margin-bottom: 8px;
            }
        `,
    },
    description: {
        size: 15,
        css: css`
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
    contactWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 16px;
        `,
    },
    contactTypography: {
        size: 15,
        weight: 600,
        color: "text.link",
        css: css`
            margin-bottom: 12px;
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
    addressInfoDisplay: {
        wrapper: {
            css: css`
                font-style: normal;
                margin-left: 22px;
            `,
        },
    },
};

const styles = orderConfirmationHelpTileStyles;

const OrderConfirmationHelpTile: FC<Props> = ({
    locationFinderPageUrl,
    locationFinderDetailsPageUrl,
    warehouse,
    quoteState,
}) => {
    const isQuote = quoteState.value?.properties.shipCode.toLowerCase() === "quote requested".toLowerCase();
    return (
        <StyledBox extendedStyles={styles.styledBox}>
            <Typography {...styles.title}>{translate("Have Questions?")}</Typography>
            <Typography {...styles.description}>
                {isQuote ? siteMessage("Keller_QuoteConfirmation_Help") : siteMessage("Keller_OrderConfirmation_Help")}
            </Typography>
            <StyledWrapper {...styles.contactWrapper}>
                {warehouse?.branchPhone && (
                    <ContactTypography
                        type="phone"
                        targetValue={warehouse.branchPhone}
                        icon={true}
                        iconProps={{ size: 14 }}
                        {...styles.contactTypography}
                    >
                        {warehouse.branchPhone}
                    </ContactTypography>
                )}
                {warehouse?.branchEmail && (
                    <ContactTypography
                        type="email"
                        targetValue={warehouse.branchEmail}
                        icon={true}
                        iconProps={{ size: 14 }}
                        {...styles.contactTypography}
                    >
                        {warehouse.branchEmail}
                    </ContactTypography>
                )}
                {warehouse?.description && (
                    <ContactTypography
                        type="branch"
                        targetValue={locationFinderDetailsPageUrl || locationFinderPageUrl}
                        icon={true}
                        iconProps={{ size: 14 }}
                        {...styles.contactTypography}
                    >
                        {warehouse.description}
                    </ContactTypography>
                )}
                <AddressInfoCondensedDisplay extendedStyles={styles.addressInfoDisplay} {...(warehouse as any)} />
            </StyledWrapper>
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(OrderConfirmationHelpTile),
    definition: {
        group: "Order Confirmation",
        allowedContexts: ["OrderConfirmationPage", "RfqConfirmationPage"],
        displayName: "Order Confirmation Help Tile",
    },
};

export default widgetModule;
